"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { localizeError } from "@/i18n/errors";
import "./chat-media.css";

/** Keep in step with src/lib/chat/attachments.ts (the server enforces these again). */
export const MAX_IMAGES = 4;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
/** Photos above this are re-encoded smaller before upload: phone cameras make 5-10 MB files. */
const DOWNSCALE_ABOVE_BYTES = 1.5 * 1024 * 1024;
const MAX_EDGE = 1800;

/** Client-side checks reuse the server's error codes so the messages come from the translation files. */
const codeOnly = (code: string) => `${code}:`;

export interface PendingImage {
  id: string;
  file: File;
  url: string;
}

/** Shrinks big JPEG/PNG/WebP photos; GIFs and small files pass through untouched. Falls back to the original on any failure. */
async function prepareImage(file: File): Promise<File> {
  if (file.type === "image/gif" || file.size <= DOWNSCALE_ABOVE_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/**
 * Images queued for the next message: add from the file picker, by pasting (Ctrl/Cmd+V a screenshot or copied image),
 * or by dropping files on the composer. Object URLs are released when images are removed or the component unmounts.
 */
export function usePendingImages() {
  const { locale } = useI18n();
  const [images, setImages] = useState<PendingImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const imagesRef = useRef<PendingImage[]>([]);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(() => () => imagesRef.current.forEach((i) => URL.revokeObjectURL(i.url)), []);

  const add = useCallback(
    async (files: File[]) => {
      const candidates = files.filter((f) => f.type.startsWith("image/") || ACCEPTED.includes(f.type));
      if (files.length > 0 && candidates.length === 0) return setError(localizeError(locale, codeOnly("CHAT_FILE_TYPE")));
      const room = MAX_IMAGES - imagesRef.current.length;
      if (candidates.length > room) setError(localizeError(locale, codeOnly("CHAT_TOO_MANY_FILES")));
      else setError(null);
      const accepted: PendingImage[] = [];
      for (const original of candidates.slice(0, Math.max(room, 0))) {
        if (!ACCEPTED.includes(original.type)) {
          setError(localizeError(locale, codeOnly("CHAT_FILE_TYPE")));
          continue;
        }
        const file = await prepareImage(original);
        if (file.size > MAX_BYTES) {
          setError(localizeError(locale, codeOnly("CHAT_FILE_TOO_LARGE")));
          continue;
        }
        accepted.push({ id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) });
      }
      if (accepted.length) setImages((prev) => [...prev, ...accepted].slice(0, MAX_IMAGES));
    },
    [locale]
  );

  const remove = useCallback((id: string) => {
    setImages((prev) => {
      prev.filter((i) => i.id === id).forEach((i) => URL.revokeObjectURL(i.url));
      return prev.filter((i) => i.id !== id);
    });
    setError(null);
  }, []);

  const clear = useCallback(() => {
    setImages((prev) => {
      prev.forEach((i) => URL.revokeObjectURL(i.url));
      return [];
    });
    setError(null);
  }, []);

  /** Attach to a textarea: pasting an image (not text) queues it and suppresses the default paste. */
  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData.items)
        .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length === 0) return;
      e.preventDefault();
      void add(files);
    },
    [add]
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault();
  }, []);
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (e.dataTransfer.files.length === 0) return;
      e.preventDefault();
      void add(Array.from(e.dataTransfer.files));
    },
    [add]
  );

  return { images, error, add, remove, clear, onPaste, onDragOver, onDrop };
}

/** Thumbnails of queued images with a remove button each. */
export function PendingStrip({ images, onRemove }: { images: PendingImage[]; onRemove: (id: string) => void }) {
  const { t } = useI18n();
  if (images.length === 0) return null;
  return (
    <div className="cm-strip">
      {images.map((img) => (
        <div key={img.id} className="cm-thumb">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={img.url} alt="" />
          <button type="button" onClick={() => onRemove(img.id)} aria-label={t("chat.media.remove")}>{"×"}</button>
        </div>
      ))}
    </div>
  );
}

/** Paperclip button that opens the file picker (multiple images; on phones, the camera/gallery). */
export function AttachButton({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled?: boolean }) {
  const { t } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={ACCEPTED.join(",")}
        multiple
        hidden
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <button type="button" className="cm-attach" disabled={disabled} onClick={() => input.current?.click()} aria-label={t("chat.media.attach")} title={t("chat.media.attach")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
      </button>
    </>
  );
}

/** Multipart body for a chat message with images: the same text fields as the JSON form, plus `files`. */
export function buildChatForm(fields: Record<string, string>, images: PendingImage[]): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  for (const img of images) form.append("files", img.file, img.file.name);
  return form;
}

/**
 * A chat image. Staff are signed in, so the browser loads it directly. Guests authenticate with chat credentials sent in
 * headers, so the image is fetched with them and shown from a blob URL (credentials never appear in an image URL).
 * Clicking opens the full image in a new tab.
 */
export function ChatImage({ id, guestHeaders }: { id: string; guestHeaders?: Record<string, string> }) {
  const { t } = useI18n();
  const [src, setSrc] = useState<string | null>(guestHeaders ? null : `/api/chat/attachments/${id}`);
  const [failed, setFailed] = useState(false);
  const headersKey = guestHeaders ? JSON.stringify(guestHeaders) : "";

  useEffect(() => {
    if (!headersKey) return;
    let url: string | null = null;
    let cancelled = false;
    fetch(`/api/public/chat/attachments/${id}`, { headers: JSON.parse(headersKey) })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("failed"))))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, headersKey]);

  if (failed) return <span className="cm-missing">{t("chat.media.unavailable")}</span>;
  if (!src) return <span className="cm-loading" aria-hidden="true" />;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="cm-image">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt={t("chat.media.imageAlt")} loading="lazy" onError={() => setFailed(true)} />
    </a>
  );
}
